import { createAsyncThunk } from "@reduxjs/toolkit";
import { walletTypesApi } from "../../api/walletTypesApi";
import {
  CreateWalletTypeRequest,
  UpdateWalletTypeRequest,
  WalletType,
} from "../../types/walletType";
import { AppDispatch, RootState } from "../store";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";

export const fetchWalletTypesThunk = createAsyncThunk<
  WalletType[],
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "walletTypes/fetchWalletTypes",
  async (_, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<WalletType[]>(
      dispatch,
      "indirect",
      () => walletTypesApi.getWalletTypes()
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to fetch wallet types");
    }
    return res.result as WalletType[];
  }
);

export const createWalletTypeThunk = createAsyncThunk<
  WalletType,
  CreateWalletTypeRequest,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "walletTypes/createWalletType",
  async (walletType, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<WalletType>(
      dispatch,
      "direct",
      () => walletTypesApi.createWalletType(walletType),
      i18n.t("requestMessage.walletTypeCreated")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to create wallet type");
    }
    await dispatch(fetchWalletTypesThunk());
    return res.result as WalletType;
  }
);

export const updateWalletTypeThunk = createAsyncThunk<
  void,
  { id: string; walletType: UpdateWalletTypeRequest },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "walletTypes/updateWalletType",
  async ({ id, walletType }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<WalletType>(
      dispatch,
      "direct",
      () => walletTypesApi.updateWalletType(id, walletType),
      i18n.t("requestMessage.walletTypeUpdated")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to update wallet type");
    }
    await dispatch(fetchWalletTypesThunk());
  }
);

export const deleteWalletTypeThunk = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "walletTypes/deleteWalletType",
  async (id, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<null>(
      dispatch,
      "direct",
      () => walletTypesApi.deleteWalletType(id),
      i18n.t("requestMessage.walletTypeDeleted")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to delete wallet type");
    }
    await dispatch(fetchWalletTypesThunk());
  }
);
